import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Box,
  Container,
  CircularProgress,
  alpha,
  useTheme,
  Typography,
  Button,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Person as PersonIcon, Save as SaveIcon } from "@mui/icons-material";

import { QK } from "../../../base/qk";
import { getCandidatesbyId, updateCandidate } from "../../../api/services/candidate";
import CandidateProfileForm from "../../../components/CandidateForm/CandidateProfileForm";
import CandidateLocationForm from "../../../components/CandidateForm/CandidateLocationForm";
import CandidateContactsForm from "../../../components/CandidateForm/CandidateContacts";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";

const CandidatesPersonal = ({ clickedId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [selectedArea, setSelectedArea] = useState([]);
  const [originalSelectedArea, setOriginalSelectedArea] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: res, isLoading: isFetching } = useQuery(
    [QK.candidate, clickedId],
    () => getCandidatesbyId(clickedId),
    {
      enabled: !!clickedId,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    const candidate = res?.data;
    if (candidate) {
      setFormData(candidate);
      setOriginalData(candidate);

      if (candidate.areas) {
        const areas = candidate.areas.map((area) => ({
          value: area.id,
          label: area.name,
        }));
        setSelectedArea(areas);
        setOriginalSelectedArea(areas);
      }
    } else {
      setFormData({});
      setOriginalData({});
      setSelectedArea([]);
      setOriginalSelectedArea([]);
    }
  }, [res, clickedId]);

  const handleImageUpload = (e) => {
    const { fileData } = e.target;
    if (fileData?.url) {
      setFormData((prev) => ({
        ...prev,
        profile_photo: fileData.url,
      }));
    }
  };

  const mutation = useMutation(
    (data) => {
      const formDataPayload = new FormData();
      
      const changedFields = {};
      
      Object.keys(data).forEach((key) => {
        if (key === "area_list") {
          const currentAreas = data[key] || [];
          const originalAreas = originalData.areas?.map(a => a.id) || [];
          
          if (JSON.stringify(currentAreas.sort()) !== JSON.stringify(originalAreas.sort())) {
            changedFields.area_list = currentAreas;
            currentAreas.forEach((id) => formDataPayload.append("area_list", id));
          }
        } else if (key !== "areas" && key !== "location" && key !== "profile_photo_data") {
          const currentValue = data[key];
          const originalValue = originalData[key];
          
          if (currentValue !== originalValue) {
            changedFields[key] = currentValue;
            if (currentValue !== null && currentValue !== undefined) {
              formDataPayload.append(key, currentValue);
            }
          }
        }
      });

      if (Object.keys(changedFields).length === 0) {
        return Promise.reject({ message: "No changes detected" });
      }

      return updateCandidate(formDataPayload, clickedId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.candidate, clickedId]);
        queryClient.invalidateQueries([QK.candidates]);
        enqueueSnackbar("Candidate updated successfully", { variant: "success" });
        setShowConfirm(false);
      },
      onError: (error) => {
        if (error.message === "No changes detected") {
          enqueueSnackbar("No changes to save", { variant: "info" });
        } else {
          console.error("Update error:", error);
        }
        setShowConfirm(false);
      },
    }
  );

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectAreaChange = (options) => {
    const selectedOptions = options || [];
    setSelectedArea(selectedOptions);
    setFormData((prev) => ({
      ...prev,
      area_list: selectedOptions.map((opt) => opt.value),
    }));
  };

  const handleSubmit = () => {
    const hasChanges = () => {
      const currentAreas = formData.area_list || [];
      const originalAreas = originalData.areas?.map(a => a.id) || [];
      
      if (JSON.stringify(currentAreas.sort()) !== JSON.stringify(originalAreas.sort())) {
        return true;
      }
      
      for (let key in formData) {
        if (key !== "areas" && key !== "location" && key !== "profile_photo_data") {
          if (formData[key] !== originalData[key]) {
            return true;
          }
        }
      }
      return false;
    };

    if (!hasChanges()) {
      enqueueSnackbar("No changes to save", { variant: "info" });
      return;
    }

    setShowConfirm(true);
  };

  if (isFetching && !formData?.id) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 0, px: 2 }}>
      <ConfirmModal
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={() => mutation.mutate(formData)}
        isLoading={mutation.isLoading}
        title="Update Personal Information"
        body="Are you sure you want to update this candidate's personal details?"
        confirmText="Update"
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
            Personal Information
          </Typography>
          <Typography variant="caption" color="#6B7280">
            Update candidate personal details
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={mutation.isLoading}
          startIcon={mutation.isLoading ? <CircularProgress size={18} /> : <SaveIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            bgcolor: "#2469F0",
            px: 3,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {mutation.isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      <Box component="form" noValidate>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
          <Box sx={{ flex: "1 1 30%", minWidth: "280px" }}>
            <CandidateProfileForm
              handleChange={handleChange}
              handleFileUpload={handleImageUpload}
              data={formData}
            />
          </Box>

          <Box sx={{ flex: "1 1 30%", minWidth: "280px" }}>
            <CandidateLocationForm
              handleChange={handleChange}
              data={formData}
              selectedArea={selectedArea}
              handleSelectAreaChange={handleSelectAreaChange}
            />
          </Box>

          <Box sx={{ flex: "1 1 30%", minWidth: "280px" }}>
            <CandidateContactsForm
              handleChange={handleChange}
              data={formData}
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default CandidatesPersonal;